/* eslint-disable react/no-unknown-property */

import { gql, useQuery, useReactiveVar } from '@apollo/client';
import { Button, Typography } from '@mui/material';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import MEDIA_UNAVAILBE from 'src/assets/mediaUnavailble.jpeg';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import viewStateVar from 'src/store/viewState';
import videojs from 'video.js';
import type Component from 'video.js/dist/types/component';
import type Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import 'videojs-markers';
import 'videojs-markers/dist/videojs.markers.css';
import 'videojs-seek-buttons';
import 'videojs-seek-buttons/dist/videojs-seek-buttons.css';
import './videojsCustomCss.css';

import { assetTimecodeMeta } from 'src/dynamoQueries/timecodes';
import { disableContextMenuElem } from 'src/helpers';
import { Timecode as SipTimecode, toFramesWithFallback } from 'src/utils/timecode';
import { interceptFrameRate } from 'src/utils/video';
import { CustomCurrentTimeDisplay, CustomDurationDisplay, CustomPlaybackRateMenuButton } from './classes';
import { createTimecodeString, replaceLastDelimiter } from './utils';

interface TimecodeMetadata {
  framerate: string;
  dropframe: boolean;
  inTimecode: string;
  finalDelimiter: string;
}

interface Marker {
  markIn?: string;
  markOut?: string;
}

interface Asset {
  id?: string;
}

interface ExtendedPlayer extends Player {
  frameRate: number;
  df: boolean;
  inpoint: string;
  finalDelimiter: string;
  currentPlaybackRate?: number;
  changePlaybackRate?: (rate: number) => void;
  currentTimecode?: (inTimeSeconds?: number, outTimeSeconds?: number, text?: string) => string | undefined;
  stop?: () => void;
  markers: {
    removeAll: () => void;
    reset: (markers: unknown[]) => void;
    add: (markers: unknown[]) => void;
  };
  children_: Array<{ playerId: string }>;
}

interface VideoJSOptions {
  preload?: string;
  autoplay?: boolean;
  controls?: boolean;
  responsive?: boolean;
  fluid?: boolean;
  aspectRatio?: string;
  playbackRates?: number[];
  audioPosterMode?: boolean;
  sources?: Array<{ src: string; type: string }>;
  poster?: string;
  plugins?: Record<string, unknown>;
  controlBar?: {
    children?: string[];
  };
}

interface VideoJSProps {
  videoUrl: string;
  trackElapsedTime?: boolean;
  options?: Partial<VideoJSOptions>;
  setPlayer?: ((player: Player) => void) | null;
  asset?: Asset;
  onEnded?: (() => void) | null;
  autoplay?: boolean;
  enableKeyBindings?: boolean;
  getVideoRef?: (ref: React.RefObject<HTMLVideoElement>) => void;
  audioPosterMode?: boolean;
  preload?: string;
  marker?: Marker;
  videoId?: string;
  poster?: string;
  className?: string;
}

const saneTCDefaults: TimecodeMetadata = {
  framerate: '29.97',
  dropframe: false,
  inTimecode: '00:00:00:00',
  finalDelimiter: ':',
};

let lastPlayedVideo: string | undefined;

const PLAYBACK_RATES = [-16, -8, -4, -2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 4, 8, 16];

function getLastPositionVariable(variable: string): string {
  return `MEDIA_LAST_POSITION_${variable}`;
}

export const VideoJS: React.FC<VideoJSProps> = ({
  videoUrl,
  trackElapsedTime,
  options,
  setPlayer = null,
  asset = {},
  onEnded = null,
  autoplay = false,
  enableKeyBindings,
  getVideoRef,
  audioPosterMode,
  preload = 'metadata',
  marker = {},
  ...props
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<ExtendedPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { showHorizontal, playingVideoId } = useReactiveVar(viewStateVar);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const generatedId = useId();
  // Sanitize useId output (e.g., ":r6:") to be a valid CSS selector (no colons, must start with letter)
  const sanitizedId = generatedId.replace(/:/g, '');
  const videoElementId = props.videoId || `videojs-${sanitizedId}`;

  const assetTimecodeQuery = () => {
    const atm = assetTimecodeMeta(true);
    const gQuery = jsonToGraphQLQuery(atm, { pretty: true });
    return gql(gQuery);
  };

  const { data } = useQuery(assetTimecodeQuery(), {
    variables: { assetId: asset.id },
    skip: !asset.id,
  });

  useEffect(() => {
    if (!playerRef.current) return;

    try {
      if (typeof playerRef.current.markers?.removeAll === 'function') {
        playerRef.current.markers.removeAll();
      }
    } catch (_) {
      // Ignore errors clearing marker overlays when plugin is not initialized
    }

    if (!marker?.markIn) {
      const markersApi = playerRef.current?.markers;
      if (markersApi && typeof markersApi.reset === 'function') {
        markersApi.reset([]);
      }
      return;
    }

    const meta = data?.assetTimecodeMetadata || saneTCDefaults;
    const fr = Number(meta.framerate || 30);
    const df = !!meta.dropframe;
    const inpoint = replaceLastDelimiter(meta.inTimecode || '00:00:00:00', df ? ';' : ':');

    const tc = new SipTimecode(fr, df);
    const startFrames = toFramesWithFallback(inpoint, fr, df);

    const markInNorm = replaceLastDelimiter(marker.markIn, df ? ';' : ':');
    const inFrames = toFramesWithFallback(markInNorm, fr, df);
    const inSeconds = tc.framesToSeconds(Math.max(0, inFrames - startFrames));

    if (marker.markOut) {
      const markOutNorm = replaceLastDelimiter(marker.markOut, df ? ';' : ':');
      const outFrames = toFramesWithFallback(markOutNorm, fr, df);
      const outSeconds = tc.framesToSeconds(Math.max(0, outFrames - startFrames));
      if (outSeconds > inSeconds) {
        playerRef.current.markers.add([
          { time: inSeconds, duration: outSeconds - inSeconds, text: 'Marker range', class: 'akomi-range' },
        ]);
      } else {
        playerRef.current.markers.add([{ time: inSeconds, text: 'Marker in', class: 'akomi-point' }]);
      }
    } else {
      playerRef.current.markers.add([{ time: inSeconds, text: 'Marker in', class: 'akomi-point' }]);
    }
  }, [marker, data]);

  videojs.registerComponent('CurrentTimeDisplay2', CustomCurrentTimeDisplay as unknown as typeof Component);
  videojs.registerComponent('CustomDurationDisplay', CustomDurationDisplay as unknown as typeof Component);
  videojs.registerComponent(
    'CustomPlaybackRateMenuButton',
    CustomPlaybackRateMenuButton as unknown as typeof Component,
  );

  const videoJsOptions: VideoJSOptions = {
    preload,
    autoplay: false,
    controls: true,
    responsive: true,
    fluid: true,
    aspectRatio: '16:9',
    playbackRates: PLAYBACK_RATES,
    audioPosterMode: audioPosterMode,
    sources: [
      {
        src: videoUrl,
        type: 'video/mp4',
      },
      {
        src: videoUrl,
        type: 'audio/mp3',
      },
    ],
    poster: props.poster,
    plugins: {},
    controlBar: {
      children: [
        'playToggle',
        'progressControl',
        'CurrentTimeDisplay2',
        'timeDivider',
        'CustomDurationDisplay',
        'volumeMenuButton',
        'volumePanel',
        'CustomPlaybackRateMenuButton',
        'PictureInPictureToggle',
        'fullscreenToggle',
      ],
    },
    ...options,
  };

  const handleAutoplay = (): void => {
    if (playerRef.current) {
      if (autoplay) {
        const promise = playerRef.current.play();

        if (promise !== undefined) {
          promise.catch((error: Error) => {
            console.log(error);
          });
        }
      }
    }
  };

  const onReady = (player: ExtendedPlayer): void => {
    player.on('play', () => {
      viewStateVar({ ...viewStateVar(), playingVideoId: props.videoId ?? null });
    });
    if (onEnded && typeof onEnded === 'function') {
      player.on('ended', () => onEnded());
    }
    handleAutoplay();
  };

  const pauseVideo = (): void => playerRef?.current?.pause();

  const playerCurrentTime = (inTimeSeconds?: number, outTimeSeconds?: number, text?: string): string | undefined => {
    const timecodeData = data?.assetTimecodeMetadata || saneTCDefaults;
    const { framerate, dropframe, inTimecode, finalDelimiter } = timecodeData;

    const framerateNum = parseFloat(framerate);

    if (typeof inTimeSeconds === 'number') {
      playerRef.current?.markers.removeAll();
      if (outTimeSeconds) {
        playerRef.current?.markers.add([
          {
            time: inTimeSeconds,
            duration: outTimeSeconds - inTimeSeconds,
            text,
            class: 'akomi-range',
          },
        ]);
      }
      playerRef.current?.currentTime(inTimeSeconds);
      return;
    }
    if (!playerRef.current) return inTimecode;
    const seconds = playerRef.current.currentTime() ?? 0;
    return createTimecodeString({
      seconds,
      frameRate: framerateNum,
      df: dropframe,
      inpoint: inTimecode,
      finalDelimiter: finalDelimiter || ':',
    });
  };

  useEffect(() => {
    containerRef?.current && disableContextMenuElem(containerRef.current);
  }, []);

  useEffect(() => {
    if (getVideoRef && videoRef.current) getVideoRef(videoRef as React.RefObject<HTMLVideoElement>);
  }, [getVideoRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleAutoplay uses playerRef.current which is a mutable ref; wrapping in useCallback would add complexity without benefit
  useEffect(() => {
    if (playerRef.current) {
      if (!autoplay) {
        playerRef.current.off('ended');
      } else if (autoplay) {
        playerRef.current.off('ended');
        playerRef.current.on('ended', () => handleAutoplay());
      }
    }
  }, [autoplay]);

  const setPlayeTimeCoderMeta = (
    assetTimecodeMetadata: TimecodeMetadata,
    playerReference: React.MutableRefObject<ExtendedPlayer | null>,
  ): React.MutableRefObject<ExtendedPlayer | null> => {
    const interceptedMetadata = interceptFrameRate(
      assetTimecodeMetadata as unknown as Parameters<typeof interceptFrameRate>[0],
    ) as unknown as TimecodeMetadata;
    const { framerate, dropframe, inTimecode, finalDelimiter } = interceptedMetadata;
    if (playerReference.current) {
      playerReference.current.frameRate = Number(framerate || 30);
      playerReference.current.df = dropframe;
      playerReference.current.inpoint = inTimecode;
      playerReference.current.finalDelimiter = finalDelimiter;
    }
    return playerReference;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: setPlayeTimeCoderMeta is stable, onReady/setPlayer are external callbacks, videoJsOptions is an object literal that would cause infinite re-renders if tracked
  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const player = videojs(videoElement, videoJsOptions, () => {
        // Use playerRef.current which is set below, before this callback runs
        if (playerRef.current) {
          onReady(playerRef.current);
        }
      }) as ExtendedPlayer;

      playerRef.current = player;

      setPlayeTimeCoderMeta(data?.assetTimecodeMetadata || saneTCDefaults, playerRef);

      (player.markers as unknown as (opts: { markerStyle: Record<string, unknown> }) => void)({
        markerStyle: {
          'z-index': 0,
        },
      });
      player.stop = () => {
        player.load();
      };
      setPlayer?.(player);
    } else {
      setPlayeTimeCoderMeta(data?.assetTimecodeMetadata || saneTCDefaults, playerRef);
    }
  }, [data]);

  const onkeydown = (e: KeyboardEvent): void => {
    if (
      e.target instanceof HTMLElement &&
      (e.target.tagName.toLowerCase() === 'input' || e.target.tagName.toLowerCase() === 'textarea')
    ) {
      return;
    }

    const resetPlayBackRate = PLAYBACK_RATES[PLAYBACK_RATES.indexOf(1)];
    const currentRate =
      playerRef.current?.currentPlaybackRate === undefined ? 1 : playerRef.current.currentPlaybackRate;
    const newPlayBackRateForward = PLAYBACK_RATES[PLAYBACK_RATES.indexOf(currentRate) + 1];
    const newPlayBackRateBackward = PLAYBACK_RATES[PLAYBACK_RATES.indexOf(currentRate) - 1];

    if (playerRef.current?.children_[0]?.playerId === lastPlayedVideo) {
      switch (e.keyCode) {
        case 74: // J
          if (newPlayBackRateBackward !== undefined && !e.shiftKey) {
            playerRef.current?.changePlaybackRate?.(newPlayBackRateBackward);
            if (playerRef.current?.paused()) {
              playerRef.current?.changePlaybackRate?.(-1);
              playerRef.current?.play();
            }
          }
          break;
        case 32: //space
        case 75: //K
          playerRef.current?.paused() ? playerRef.current?.play() : playerRef.current?.pause();
          playerRef.current?.changePlaybackRate?.(resetPlayBackRate);
          break;
        case 76: //L
          if (newPlayBackRateForward !== undefined && !e.shiftKey) {
            playerRef.current?.changePlaybackRate?.(newPlayBackRateForward);
            if (playerRef.current?.paused()) playerRef.current?.play();
          }
          break;
        case 37: //Left
          if (playerRef.current) {
            playerRef.current.currentTime((playerRef.current.currentTime() ?? 0) - 1);
          }
          break;

        case 39: //Right
          if (playerRef.current) {
            playerRef.current.currentTime((playerRef.current.currentTime() ?? 0) + 1);
          }
          break;

        default:
          break;
      }
    }
  };

  const onPlayVideo = (e: React.SyntheticEvent<HTMLVideoElement>): void => {
    lastPlayedVideo = (e.target as HTMLVideoElement & { playerId?: string }).playerId;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: onkeydown uses playerRef.current which is a mutable ref; function identity changes are intentional to get latest player state
  useEffect(() => {
    enableKeyBindings
      ? document.addEventListener('keydown', onkeydown)
      : document.removeEventListener('keydown', onkeydown);

    return () => {
      document.removeEventListener('keydown', onkeydown);
    };
  }, [enableKeyBindings]);

  function saveVideoPostition(): void {
    if (asset.id && playerRef.current && (playerRef.current.currentTime() ?? 0) > 0) {
      const timecode = playerRef.current.currentTimecode?.();
      if (typeof timecode === 'string') {
        localStorage.setItem(getLastPositionVariable(asset.id), timecode);
      }
    }
  }

  const checkVideoLastPosition = (): void => {
    if (trackElapsedTime && asset.id && localStorage.getItem(getLastPositionVariable(asset.id))) {
      setShowResumeModal(true);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: checkVideoLastPosition and saveVideoPostition use playerRef.current and asset.id; these are called once on mount/unmount
  useEffect(() => {
    if (trackElapsedTime) window.onbeforeunload = saveVideoPostition;
    checkVideoLastPosition();
    return () => {
      if (trackElapsedTime) saveVideoPostition();
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [trackElapsedTime]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pauseVideo is a simple function that calls playerRef.current.pause(); no need to track as dependency
  useEffect(() => {
    if (playingVideoId !== props.videoId) pauseVideo();
  }, [playingVideoId, props.videoId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pauseVideo is a simple function that calls playerRef.current.pause(); no need to track as dependency
  useEffect(() => {
    if (playingVideoId === props.videoId && showHorizontal) pauseVideo();
  }, [showHorizontal, playingVideoId, props.videoId]);

  useEffect(() => {
    if (!playerRef.current) {
      return;
    }
    // Use the player reference directly instead of looking up by id
    // (video.js assigns numeric ids which are invalid CSS selectors)
    playerRef.current.src([
      {
        src: videoUrl,
        type: 'video/mp4',
      },
      {
        src: videoUrl,
        type: 'audio/mp3',
      },
    ]);
    playerRef.current.poster(props.poster || '');
  }, [videoUrl, props.poster]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: playerCurrentTime uses data from query and playerRef; assigning to player.currentTimecode should use latest function
  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.currentTimecode = playerCurrentTime;
    }
  }, [data]);

  const onErrorVideoLoad = (): void => {
    setTimeout(() => {
      (
        playerRef.current as unknown as {
          errorDisplay: { close: () => void };
          posterImage: { setSrc: (src: string) => void };
        }
      )?.errorDisplay?.close();
      (playerRef.current as unknown as { posterImage: { setSrc: (src: string) => void } })?.posterImage?.setSrc(
        MEDIA_UNAVAILBE,
      );
    }, 0);
  };

  const onResumeVideo = (): void => {
    if (asset.id) {
      const savedTime = localStorage.getItem(getLastPositionVariable(asset.id));
      if (savedTime) {
        playerRef.current?.currentTimecode?.(parseFloat(savedTime));
      }
    }
    playerRef.current?.play();
    setShowResumeModal(false);
  };

  const onPlayFromStart = (): void => {
    setShowResumeModal(false);
    playerRef.current?.play();
  };

  return (
    <div style={{ cursor: 'move' }} data-vjs-player ref={containerRef}>
      <AkomiDialog
        open={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        title="Resume Playback"
        maxWidth="xs"
        actions={
          <AkomiDialogActions variant="confirm">
            <Button onClick={onPlayFromStart} color="secondary">
              Start from beginning
            </Button>
            <Button onClick={onResumeVideo} variant="contained" color="primary">
              Resume video
            </Button>
          </AkomiDialogActions>
        }
      >
        <Typography sx={{ textAlign: 'center', py: 1 }}>Do you want to start from where you left off?</Typography>
      </AkomiDialog>
      {/* biome-ignore lint/a11y/useMediaCaption: Captions are loaded dynamically by video.js player when available */}
      <video
        id={videoElementId}
        onError={onErrorVideoLoad}
        ref={videoRef}
        onPlay={onPlayVideo}
        className={`video-js vjs-big-play-centered videojs-youtube ${props.className ? props.className : ''}`}
      />
    </div>
  );
};

export default VideoJS;
